import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Search } from "lucide-react";
import ProductCard from "@/components/ProductCard";
import { api } from "@/lib/api";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { CATEGORIES } from "@/lib/categories";

const CATS = [{ key: "all", label: "All" }, ...CATEGORIES.map((c) => ({ key: c.key, label: c.label }))];

export default function Shop() {
  const [params, setParams] = useSearchParams();
  const category = params.get("category") || "all";
  const urlSearch = params.get("search") || "";
  const urlSort = params.get("sort") || "featured";
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState(urlSearch);
  const [sort, setSort] = useState(urlSort);

  useEffect(() => {
    setSearch(urlSearch);
  }, [urlSearch]);

  useEffect(() => {
    setSort(urlSort);
  }, [urlSort]);

  useEffect(() => {
    setLoading(true);
    api
      .get("/products", { params: { category, search: search || undefined, sort } })
      .then(({ data }) => setProducts(data))
      .finally(() => setLoading(false));
  }, [category, search, sort]);

  const setCategory = (key) => {
    const next = new URLSearchParams(params);
    if (key === "all") next.delete("category");
    else next.set("category", key);
    setParams(next);
  };

  return (
    <div className="min-h-screen pt-[68px] lg:pt-[116px]" data-testid="shop-page">
      {/* Header */}
      <div className="section-dark px-5 py-16 sm:px-8">
        <div className="mx-auto max-w-[1400px]">
          <p className="mb-3 font-mono text-xs uppercase tracking-overline text-[#34D8A0]">The Catalog</p>
          <h1 className="font-display text-5xl font-extrabold tracking-tighter sm:text-7xl">Formulas</h1>
        </div>
      </div>

      <div className="mx-auto max-w-[1400px] px-5 py-10 sm:px-8">
        {/* Controls */}
        <div className="mb-10 flex flex-col gap-6 border-b border-black/10 pb-6 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex flex-wrap gap-2">
            {CATS.map((c) => (
              <button
                key={c.key}
                onClick={() => setCategory(c.key)}
                className={`rounded-full border px-5 py-2 font-mono text-xs uppercase tracking-overline transition-colors ${
                  category === c.key
                    ? "border-black bg-black text-white"
                    : "border-black/15 text-black/60 hover:border-black"
                }`}
                data-testid={`filter-${c.key}`}
              >
                {c.label}
              </button>
            ))}
          </div>

          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2 border-b border-black/20 pb-1">
              <Search className="h-4 w-4 text-black/40" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search formulas"
                className="w-40 bg-transparent text-sm focus:outline-none"
                data-testid="search-input"
              />
            </div>
            <Select value={sort} onValueChange={setSort}>
              <SelectTrigger className="w-[160px] rounded-full" data-testid="sort-select">
                <SelectValue placeholder="Sort" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="featured">Featured</SelectItem>
                <SelectItem value="price_low">Price: Low → High</SelectItem>
                <SelectItem value="price_high">Price: High → Low</SelectItem>
                <SelectItem value="rating">Top Rated</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Grid */}
        {loading ? (
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="aspect-[3/4] animate-pulse bg-black/5" />
            ))}
          </div>
        ) : products.length === 0 ? (
          <div className="py-24 text-center font-mono text-sm uppercase tracking-overline text-black/40" data-testid="no-products">
            No formulas found
          </div>
        ) : (
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4" data-testid="products-grid">
            {products.map((p, i) => (
              <ProductCard key={p.id} product={p} index={i} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
